import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Flex, Spin, Table, Tabs, type TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';

import type { FilterOption } from '@shared/lib/types';
import type { DateFilterValue } from '@shared/ui/vanilla-calendar/lib/types';
import { PeriodShortSelector } from '@shared/ui/selector/period-short-select/period-short-select';
import { Select, Text } from '@shared/ui/antd';
import { useDebouncedFunc } from '@shared/hooks/use-debounced-func';

import type { CreditTransactionType } from '@shared/api/endpoints/credit/credit-endpoints';
import type { PeriodPreset } from '../../lib/constants';
import {
  buildDefaultMonth,
  buildDefaultToday,
  buildDefaultWeek,
  buildDefaultYesterday,
} from '../../lib/utils';
import { useCreditTransactions } from '../../hooks/use-credit-transactions';

import {
  buildTransactionColumns,
  buildTransactionTypeOptions,
  SCROLL_THRESHOLD,
  TABLE_SCROLL_Y,
} from './lib/constants';
import billingStyles from '../../billing.module.scss';
import styles from './transactions-table.module.scss';

export const TransactionsTable = () => {
  const { t: tBilling } = useTranslation('translation', { keyPrefix: 'billing' });
  const { t: tCommon } = useTranslation('translation');

  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('today');
  const [periodValue, setPeriodValue] = useState<FilterOption<DateFilterValue>[] | null>(() =>
    buildDefaultToday(tCommon('common.date.today')),
  );

  const periodTabs: TabsProps['items'] = useMemo(
    () => [
      { key: 'today', label: tCommon('common.date.today') },
      { key: 'yesterday', label: tCommon('common.date.yesterday') },
      { key: 'week', label: tCommon('common.calendar.lastWeek') },
      { key: 'month', label: tCommon('common.calendar.lastMonth') },
    ],
    [tCommon],
  );

  const handlePeriodPresetChange = useCallback(
    (next: PeriodPreset) => {
      setPeriodPreset(next);
      if (next === 'today') {
        setPeriodValue(buildDefaultToday(tCommon('common.date.today')));
        return;
      }
      if (next === 'yesterday') {
        setPeriodValue(buildDefaultYesterday(tCommon('common.date.yesterday')));
        return;
      }
      if (next === 'month') {
        setPeriodValue(buildDefaultMonth(tCommon('common.calendar.lastMonth')));
        return;
      }
      setPeriodValue(buildDefaultWeek(tCommon('common.calendar.lastWeek')));
    },
    [tCommon],
  );

  const handlePeriodChange = useCallback((val: FilterOption<DateFilterValue>[]) => {
    setPeriodValue(val);
  }, []);

  const [selectedTypes, setSelectedTypes] = useState<CreditTransactionType[]>([]);
  const typeLabel = tBilling('transactions.filters.typeLabel');

  const typeOptions = useMemo(() => buildTransactionTypeOptions(tBilling), [tBilling]);
  const maxTagPlaceholder = useMemo(
    () => (
      <Text variant="sm" fontWeight={400} style={{ color: '#1435C7' }}>
        {typeLabel} ({selectedTypes.length})
      </Text>
    ),
    [selectedTypes.length, typeLabel],
  );

  const { transactions, hasMore, isFetching, isLoading, fetchNextData } = useCreditTransactions({
    periodValue,
    types: selectedTypes,
  });

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const handleScroll = useDebouncedFunc(() => {
    if (!hasMore || isFetching || isLoading) return;
    const container = tableWrapperRef.current?.querySelector('.ant-table-body') as HTMLDivElement;
    if (!container) return;

    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (remaining <= SCROLL_THRESHOLD) {
      fetchNextData();
    }
  }, 150);

  useEffect(() => {
    const container = tableWrapperRef.current?.querySelector('.ant-table-body') as HTMLDivElement;
    if (!container) return;

    const onScroll = () => handleScroll();
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [handleScroll]);

  useEffect(() => {
    handleScroll();
  }, [transactions.length, handleScroll]);

  const columns = useMemo(() => buildTransactionColumns(tBilling), [tBilling]);
  const scrollY = transactions.length > 6 ? TABLE_SCROLL_Y : undefined;

  return (
    <Flex vertical gap={16} className={billingStyles.history}>
      <Text variant="lg" fontWeight={500}>
        {tBilling('transactions.title')}
      </Text>

      <Flex align="center" justify="space-between" className={billingStyles.historyControls}>
        <Tabs
          className={billingStyles.periodTabs}
          activeKey={periodPreset}
          onChange={(key) => handlePeriodPresetChange(key as PeriodPreset)}
          items={periodTabs}
        />

        <Flex align="center" gap={12} className={billingStyles.historyActions}>
          <Select
            selectMode="multiple"
            placeholder={typeLabel}
            value={selectedTypes}
            options={typeOptions}
            onChange={(value) => setSelectedTypes(value as CreditTransactionType[])}
            maxTagCount={0}
            maxTagPlaceholder={maxTagPlaceholder}
            showSearch={false}
            optionRender={(option) => (
              <Flex align="center" gap={8} style={{ height: '100%' }}>
                <Checkbox checked={selectedTypes.includes(option.value as CreditTransactionType)} />
                <Text variant="sm" fontWeight={400}>
                  {option.label}
                </Text>
              </Flex>
            )}
            dropdownRender={(menu) => <div style={{ padding: 4 }}>{menu}</div>}
            className={styles.typeSelect}
            style={{ width: 200, height: 32 }}
          />
          <PeriodShortSelector
            value={periodValue}
            onChange={handlePeriodChange}
            containerStyles={{ height: 26 }}
          />
        </Flex>
      </Flex>

      <div className={styles.wrapper} ref={tableWrapperRef}>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          pagination={false}
          loading={isLoading}
          scroll={scrollY ? { y: scrollY } : undefined}
          className={styles.table}
          footer={
            isFetching && transactions.length > 0
              ? () => (
                  <Flex align="center" justify="center" className={styles.loaderRow}>
                    <Spin />
                  </Flex>
                )
              : undefined
          }
        />
      </div>
    </Flex>
  );
};
